/**
 * CampaignDetails
 *
 * Manages survey questions and optional YouTube explainer video, with URL
 * normalization and read-only support.
 */
import React, { useState, useEffect } from 'react';
import { Label } from '../../ui/inputs/label';
import { SurveyQuestions } from '../components/SurveyQuestions';
import { HelpCircle, X, Info } from 'lucide-react';
import { Switch } from '../../ui/inputs/switch';
import { ExplainerVideoHelpModal } from '../modals/ExplainerVideoHelpModal';

export function CampaignDetails({
  formData,
  handleInputChange,
  surveyQuestions,
  handleAddQuestion,
  handleRemoveQuestion,
  handleQuestionChange,
  setIsHelpOpen,
  aiGeneratedFields,
  explainerVideo,
  setExplainerVideo,
  hasExplainerVideo,
  setHasExplainerVideo,
  readOnly = false
}) {
  const [isExplainerHelpOpen, setIsExplainerHelpOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [formattedUrl, setFormattedUrl] = useState('');

  // Debug logging when component mounts or surveyQuestions changes
  React.useEffect(() => {
    console.log('CampaignDetails component with survey questions:', {
      questionCount: surveyQuestions?.length || 0,
      questionsValid: Array.isArray(surveyQuestions) && 
                    surveyQuestions.length > 0 && 
                    surveyQuestions.some(q => q && q.question && q.question.trim()),
      questions: surveyQuestions?.map(q => ({
        id: q.id,
        hasContent: Boolean(q.question && q.question.trim()),
        contentPreview: q.question ? 
          (q.question.length > 20 ? q.question.substring(0, 20) + '...' : q.question) 
          : 'empty'
      })) || []
    });
  }, [surveyQuestions]);

  // Set initial videoUrl if explainerVideo is already set (for editing)
  useEffect(() => {
    if (explainerVideo && typeof explainerVideo === 'string' && explainerVideo.startsWith('http')) {
      console.log('CampaignDetails: Setting video URL from explainerVideo prop:', explainerVideo);
      setVideoUrl(explainerVideo.replace(/^(https?:\/\/)?(www\.)?/, ''));
      setFormattedUrl(explainerVideo);
    }
  }, [explainerVideo]);

  // Format the URL with https:// prefix
  useEffect(() => {
    if (!videoUrl) {
      setFormattedUrl('');
      return;
    }
    
    let processedUrl = videoUrl.trim();
    
    // Remove protocol if user entered it
    processedUrl = processedUrl.replace(/^(https?:\/\/)?(www\.)?/, '');
    
    // Remove trailing slashes
    processedUrl = processedUrl.replace(/\/+$/, '');
    
    // Set the formatted URL with https:// prefix
    setFormattedUrl(`https://${processedUrl}`);
    
    // Update the explainerVideo in parent component
    setExplainerVideo(`https://${processedUrl}`);
  }, [videoUrl]);

  const handleExplainerToggle = (checked) => {
    setHasExplainerVideo(checked);
    // If toggling off, clear the video
    if (!checked) {
      setExplainerVideo(null);
      setVideoUrl('');
    }
  };

  const handleVideoUrlChange = (e) => {
    // Strip out http:// or https:// to prevent duplication since we display https:// separately
    const inputValue = e.target.value;
    const cleanedValue = inputValue.replace(/^(https?:\/\/)?(www\.)?/, '');
    setVideoUrl(cleanedValue);
  };

  const handleRemoveExplainerVideo = () => {
    setExplainerVideo(null);
    setVideoUrl('');
  };

  return (
    <div className="space-y-6">
      {/* Explainer Video Section with adjusted height to prevent layout shifts */}
      <div className="space-y-4" style={{ minHeight: hasExplainerVideo ? '200px' : '60px' }}>
        <div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Label htmlFor="has-explainer-video" className="cursor-pointer flex items-center">
                Add Youtube Explainer Video
                <button 
                  onClick={() => setIsExplainerHelpOpen(true)}
                  className="ml-2 inline-flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                  aria-label="Learn more about explainer videos"
                >
                  <HelpCircle className="h-4 w-4" />
                </button>
              </Label>
            </div>
            <Switch 
              id="has-explainer-video" 
              checked={hasExplainerVideo}
              onCheckedChange={handleExplainerToggle}
              disabled={readOnly}
            />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Add a YouTube video link to introduce your survey and provide context for participants.
          </p>
        </div>
        
        {hasExplainerVideo && (
          <div className="mt-3">            
            <div>
              <div className="flex items-center rounded-lg border border-gray-200 bg-white px-4 text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white">
                <span className="text-gray-500 dark:text-gray-400">https://</span>
                <input
                  id="youtube-url"
                  type="text"
                  value={videoUrl}
                  onChange={handleVideoUrlChange}
                  placeholder="youtube.com/watch?v=xxxxxxxxxxx"
                  className="flex-1 border-0 bg-transparent py-3 focus:outline-none focus:ring-0 dark:text-white dark:placeholder:text-gray-400"
                  disabled={readOnly}
                />
                {videoUrl && !readOnly && (
                  <button
                    onClick={handleRemoveExplainerVideo}
                    className="ml-2 text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
                    aria-label="Remove video"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="mt-1 flex items-center text-xs text-gray-500 dark:text-gray-400">
                <Info className="h-3.5 w-3.5 mr-1 text-gray-400 dark:text-gray-500" />
                Paste a YouTube video URL. The video will be visible in the phone preview. At least one survey question is still required.
              </div>
            </div>
          </div>
        )}
      </div>

      <SurveyQuestions
        surveyQuestions={surveyQuestions}
        handleAddQuestion={handleAddQuestion}
        handleRemoveQuestion={handleRemoveQuestion}
        handleQuestionChange={(id, value) => {
          // Ensure we have a non-null value even if empty string
          const safeValue = value === null ? '' : value;
          handleQuestionChange(id, safeValue);
        }}
        setIsHelpOpen={setIsHelpOpen}
        aiGeneratedFields={aiGeneratedFields}
        className={hasExplainerVideo ? "-mt-2" : ""}
        readOnly={readOnly}
      />

      <ExplainerVideoHelpModal 
        isOpen={isExplainerHelpOpen} 
        onClose={() => setIsExplainerHelpOpen(false)} 
      />
    </div>
  );
}